import {
  formatarCnpj,
  percentualDe,
  sociosVigentes,
  type Sociedade,
  type TipoSocietario,
} from '@social/shared';
import { emData, type Periodo } from '../componentes/periodo-rapido';

/** Uma sociedade, enriquecida com o que a leva de pessoas resolve por fora. */
export interface SociedadeResumo extends Sociedade {
  /**
   * Nomes dos sócios, na ordem de `socios`.
   *
   * `Sociedade.socios` (packages/shared/src/dominio/sociedade.ts) só guarda
   * `pessoaId` — é o estado jurídico, não um cache de nome. Quem exibe ou
   * filtra por nome precisa de `Pessoa`, que mora numa listagem própria
   * (`GET /pessoas`). Resolvido uma vez em `sociedades.tsx`, contra a MESMA
   * leva de 200 pessoas que o teto do Step 1 libera — e não um `GET /pessoas/:id`
   * por sócio, que reintroduziria o N+1 que a Task já carrega para as sociedades.
   */
  nomesSocios: readonly string[];
}

/** `Sociedade` (do fetch) + o mapa de nomes → `SociedadeResumo` (o que a tela usa). */
export function paraResumo(
  s: Sociedade,
  nomePorPessoaId: ReadonlyMap<string, string>,
): SociedadeResumo {
  return {
    ...s,
    nomesSocios: s.socios.map((p) => nomePorPessoaId.get(p.pessoaId) ?? '—'),
  };
}

export const moeda = (centavos: number): string =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const dataBR = (iso: string): string =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR');

/** `'33483404000132'` → `'33.483.404/0001-32'`. `'—'` quando não há CNPJ — é o caso que importa. */
export const cnpjBR = (digitos: string | undefined): string =>
  digitos ? formatarCnpj(digitos) : '—';

/** `true` quando a sociedade não tem CNPJ — constituição ainda não registrada na Junta. */
export function semRegistro(s: Sociedade): boolean {
  return !s.cnpj;
}

/**
 * Sócios ATUAIS — nunca tiveram saída registrada.
 *
 * `sociosVigentes(s)` sem data usa o padrão `'9999-12-31'` do domínio, que na
 * prática significa "sem `saida`": é o jeito de perguntar "quem ainda está
 * dentro" sem precisar da data de hoje. Column e KPI usam este número, não
 * `s.socios.length`, que contaria sócio retirado como sócio de hoje.
 */
export function sociosAtuais(s: Sociedade): number {
  return sociosVigentes(s).length;
}

/**
 * Percentual de cada sócio VIGENTE, com o RESÍDUO do truncamento absorvido
 * pelo de maior quota.
 *
 * `formatarPercentual` (packages/shared/src/redacao/formato.ts) trunca de
 * propósito — arredondar faz três sócios em partes iguais somarem 100,01, e a
 * Junta confere essa soma. O comentário de lá aponta quem resolve o resto:
 * "o resíduo é absorvido pelo maior sócio na montagem da tabela" — é este
 * mapa. Sócio sem participação vigente (retirado) fica de fora: a soma só
 * precisa fechar em 100 entre quem está dentro hoje.
 */
export function percentuaisVigentes(s: Sociedade): ReadonlyMap<string, number> {
  const vigentes = sociosVigentes(s);
  const mapa = new Map<string, number>();
  if (vigentes.length === 0) return mapa;

  let somaTruncada = 0;
  for (const p of vigentes) {
    const truncado = Math.trunc(percentualDe(s, p.pessoaId) * 100) / 100;
    mapa.set(p.pessoaId, truncado);
    somaTruncada += truncado;
  }

  const residuo = Math.round((100 - somaTruncada) * 100) / 100;
  if (residuo !== 0) {
    const maior = vigentes.reduce((a, b) => (b.quotas > a.quotas ? b : a));
    // `Math.round(... * 100) / 100`, de novo: `33.33 + 0.01` em ponto
    // flutuante dá `33.339999999999996`, não `33.34` — e essa sujeira
    // reapareceria na tabela se o segundo arredondamento não limpasse.
    const ajustado = Math.round(((mapa.get(maior.pessoaId) ?? 0) + residuo) * 100) / 100;
    mapa.set(maior.pessoaId, ajustado);
  }
  return mapa;
}

/**
 * O tom do selo de tipo societário.
 *
 * Nenhum tipo é "melhor" que outro — SLU, LTDA, EI, SS, SA, NC e CS são
 * ESCOLHAS, não estados. Por isso nada de `ok`/`alerta`/`erro` aqui: essas
 * cores dizem "isto está bem" ou "isto precisa de atenção", e um tipo
 * societário não é nem uma coisa nem outra. Os sete tons abaixo são leituras
 * neutras da MESMA escala de cinza (mais a marca, que também não carrega
 * sentido de estado) — distintos o bastante para separar os sete tipos a
 * olho, sem inventar um semáforo que não existe no domínio.
 *
 * `linha` e `sutil` reaproveitam combinações já usadas em `ui.tsx` e
 * `tabela.tsx` — a auditoria de contraste (`npm run contraste`) já as mede.
 */
export type Tom =
  'neutro' | 'marca' | 'contorno' | 'claro' | 'escuro' | 'linha' | 'sutil';

// Em v4 `border` sozinha herda `currentColor` — cada tom PRECISA de `border-*`
// explícita, senão o contorno sai na cor do TEXTO. E com `border` na base do
// selo, `ring-1` somaria um segundo traço: os dois tons que usavam `ring`
// migram para `border`.
const CLASSES_TOM: Record<Tom, string> = {
  neutro: 'border-tinta-300 bg-tinta-100 text-tinta-700',
  marca: 'border-marca-400 bg-marca-100 text-marca-700',
  contorno: 'border-tinta-400 bg-superficie text-tinta-700',
  claro: 'border-tinta-300 bg-tinta-50 text-tinta-900',
  // `border-transparent`, não `border-tinta-900`: uma borda da MESMA cor do
  // fundo criaria um par a==b no medidor de contraste.
  escuro: 'border-transparent bg-tinta-900 text-tinta-50',
  linha: 'border-tinta-400 bg-superficie text-tinta-900',
  sutil: 'border-tinta-300 bg-superficie-2 text-tinta-700',
};

export const TOM_TIPO: Record<TipoSocietario, Tom> = {
  SLU: 'neutro',
  LTDA: 'marca',
  EI: 'contorno',
  SS: 'claro',
  SA: 'escuro',
  NC: 'linha',
  CS: 'sutil',
};

/** Classe do selo de tipo — pronta para as duas visões (tabela e cartão). */
export function classeSeloTipo(tipo: TipoSocietario): string {
  // Forma do Pill do isotec: contorno + negrito + `whitespace-nowrap`. Só a
  // FORMA migra — a escala neutra e os tons são nossos.
  return `inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-bold ${CLASSES_TOM[TOM_TIPO[tipo]]}`;
}

/**
 * O período vira um intervalo `[de, ate]` fechado — janela móvel ou intervalo
 * escolhido a dedo. É a tradução que falta entre `Periodo` (o que o
 * `PeriodoRapido` devolve) e o filtro de fato: nem a origem no Adonai nem este
 * projeto tinham essa função pronta.
 *
 * NUNCA produz `de > ate`: um intervalo invertido faria todo `BETWEEN` em
 * memória dar vazio sem avisar por quê. Quando `inicio`/`fim` chegam trocados
 * (alguém editou os campos "De"/"Até" fora de ordem), a função devolve o par
 * na ordem certa em vez de propagar a inversão.
 */
export function limitesDoPeriodo(periodo: Periodo): { de: string; ate: string } {
  if (periodo.inicio && periodo.fim) {
    return periodo.inicio <= periodo.fim
      ? { de: periodo.inicio, ate: periodo.fim }
      : { de: periodo.fim, ate: periodo.inicio };
  }
  const hoje = new Date();
  // `dias: 0` é "sem período": abre a data mínima possível de propósito, em
  // vez de não filtrar nada por engano.
  if (!periodo.dias) return { de: '0000-01-01', ate: emData(hoje) };
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - periodo.dias);
  return { de: emData(inicio), ate: emData(hoje) };
}
